import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { invitationApi } from '../services/api';
import { loginRequest, isEntraAuth } from '../auth/authConfig';
import { useAuth } from '../context/AuthContext';
import { formatTimestampDate } from '../utils/dateTime';
import wmLogo from '../assets/logos/WM-Logo-CB-White-Header.png';

// phase: loading | valid | redirecting | completing | finishing | done | expired | accepted | error

const INVITE_STEPS = [
  { label: 'Invitation validated'              },
  { label: 'Signing you in with Microsoft'     },
  { label: 'Activating your account'           },
  { label: 'Loading your workspace'            },
];

// Map phase → how many steps are done (0-based: idx < doneCount = done, idx === doneCount = active)
const PHASE_STEP = { redirecting: 1, completing: 2, finishing: 3, done: 4 };

const AcceptInvitationPage = () => {
  const { token }        = useParams();
  const navigate         = useNavigate();
  const { instance, accounts } = useMsal();
  const { refreshUsers } = useAuth();

  const [phase, setPhase]       = useState('loading');
  const [invite, setInvite]     = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Step 1 — validate token on mount
  useEffect(() => {
    invitationApi.validate(token)
      .then(res => {
        const d = res.data;
        if (d.status === 'pending') {
          setInvite(d);
          setPhase('valid');
        } else if (d.status === 'accepted') {
          setPhase('accepted');
        } else if (d.status === 'expired') {
          setPhase('expired');
        } else {
          setPhase('error');
          setErrorMsg('Unknown invitation status.');
        }
      })
      .catch(() => {
        setPhase('error');
        setErrorMsg('Invitation not found or has been removed.');
      });
  }, [token]);

  // Step 2 — if MSAL lands us back here after login redirect, auto-complete accept
  useEffect(() => {
    if (phase === 'valid' && accounts.length > 0) {
      completeAccept();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, accounts]);

  const handleAccept = async () => {
    if (!isEntraAuth()) {
      setPhase('error');
      setErrorMsg('Authentication not configured. Contact your administrator.');
      return;
    }
    setPhase('redirecting');
    try {
      let tokenResult;
      try {
        tokenResult = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      } catch {
        // Silent failed — redirect (page reloads back here with accounts populated)
        await instance.loginRedirect({ ...loginRequest, redirectUri: window.location.href });
        return;
      }
      // Silent succeeded — skip redirect phase, go straight to completing
      setPhase('completing');
      await doAccept(tokenResult.accessToken);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to accept invitation.');
    }
  };

  const completeAccept = async () => {
    setPhase('completing');
    try {
      const tokenResult = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      await doAccept(tokenResult.accessToken);
    } catch (err) {
      setPhase('error');
      setErrorMsg(err.response?.data?.error || err.message || 'Failed to complete invitation.');
    }
  };

  const doAccept = async (accessToken) => {
    await invitationApi.accept(token, accessToken);
    setPhase('finishing');
    await new Promise(r => setTimeout(r, 500));
    await refreshUsers();
    setPhase('done');
    // Navigate to DB picker — will auto-select if only one DB
    setTimeout(() => navigate('/select-database', { replace: true }), 800);
  };

  // ── Layout wrapper ────────────────────────────────────────────────────────

  const wrap = (content) => (
    <div className="stui-auth-gate-backdrop">
      <div className="stui-auth-gate-card">
        {/* Header */}
        <div className="stui-auth-gate-header">
          <img src={wmLogo} alt="Wunderlich-Malec" className="stui-auth-gate-logo" />
          <div className="stui-auth-gate-header-divider" />
          <div>
            <div className="stui-auth-gate-title">DAWN</div>
            <div className="stui-auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKING</div>
          </div>
        </div>
        {/* Body */}
        <div className="stui-auth-gate-body">
          {content}
        </div>
      </div>
    </div>
  );

  // ── Step-progress view (used during redirecting / completing / finishing / done) ──

  const renderSteps = () => {
    const doneCount = PHASE_STEP[phase] ?? 0;
    return (
      <div className="stui-auth-gate-steps">
        {INVITE_STEPS.map((step, idx) => {
          const isDone   = idx < doneCount || phase === 'done';
          const isActive = !isDone && idx === doneCount;
          const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
          return (
            <div key={idx} className={`stui-auth-gate-step ${state}`}>
              <div className="stui-auth-gate-step-icon">
                {isDone ? (
                  <CheckCircle size={20} color="#1a5568" />
                ) : isActive ? (
                  <Loader2 size={20} className="stui-spinner" />
                ) : (
                  <div className="stui-auth-gate-step-circle" />
                )}
              </div>
              <span className="stui-auth-gate-step-label">{step.label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Phase renders ─────────────────────────────────────────────────────────

  if (phase === 'loading') return wrap(
    <div className="stui-auth-gate-deadend">
      <Loader2 size={28} className="stui-spinner" />
      <div className="stui-auth-gate-deadend-text">Validating invitation...</div>
    </div>
  );

  if (['redirecting', 'completing', 'finishing', 'done'].includes(phase)) return wrap(renderSteps());

  if (phase === 'accepted') return wrap(
    <div className="stui-auth-gate-deadend">
      <CheckCircle size={44} color="#2563eb" />
      <div className="stui-auth-gate-deadend-title">Already accepted</div>
      <div className="stui-auth-gate-deadend-text">This invitation has already been used.</div>
      <button onClick={() => navigate('/')} className="stui-btn stui-btn-primary">Go to DAWN</button>
    </div>
  );

  if (phase === 'expired') return wrap(
    <div className="stui-auth-gate-deadend">
      <Clock size={44} color="#f59e0b" />
      <div className="stui-auth-gate-deadend-title">Invitation expired</div>
      <div className="stui-auth-gate-deadend-text">This invitation link has expired. Contact your administrator for a new one.</div>
    </div>
  );

  if (phase === 'error') return wrap(
    <div className="stui-auth-gate-deadend">
      <XCircle size={44} className="stui-text-error" />
      <div className="stui-auth-gate-deadend-title">Something went wrong</div>
      <div className="stui-auth-gate-deadend-text">{errorMsg}</div>
      <button onClick={() => navigate('/')} className="stui-btn stui-btn-secondary">Back to DAWN</button>
    </div>
  );

  // phase === 'valid'
  const expiryDate = invite?.expiresAt
    ? formatTimestampDate(invite.expiresAt, { style: 'long', weekday: false })
    : '';

  return wrap(
    <div className="stui-auth-gate-deadend">
      <div className="stui-auth-gate-deadend-title">Hi {invite?.displayName},</div>
      <div className="stui-auth-gate-deadend-text">
        You've been invited to access DAWN
        {invite?.orgName ? ` under ${invite.orgName}` : ''}.
        Sign in with your Microsoft account (<strong>{invite?.email}</strong>) to activate your access.
      </div>
      <button onClick={handleAccept} className="stui-btn stui-btn-primary">
        Accept &amp; Sign In
      </button>
      {expiryDate && (
        <div className="stui-label-xs-muted">
          Invitation expires {expiryDate}
        </div>
      )}
    </div>
  );
};

export default AcceptInvitationPage;
